import React, { useEffect, useState } from "react";
import { Link, useLocation, Navigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useLicense } from "../context/LicenseContext";
import { PRODUCT_NAME, useBranding } from "../context/BrandingContext";
import { useCustomer } from "../context/CustomerContext";
import AIAssist from "./AIAssist";
import api from "../api/client";
import ChangePasswordModal from "./ChangePasswordModal";
import CustomerSwitcher from "./CustomerSwitcher";

// Sidebar navigation as a two-level tree. Leaves are normal routes;
// groups are non-routable headers with collapsible children. We keep
// the type loose (`children?` on every node) so an admin's eyes can
// scan the whole tree in one place.
type NavLeaf = {
  path: string;
  label: string;
  icon: string;
  adminOnly?: boolean;
};
type NavGroup = {
  groupKey: string; // stable id used as the localStorage key for expand state
  label: string;
  icon: string;
  adminOnly?: boolean;
  children: NavLeaf[];
};
type NavEntry = NavLeaf | NavGroup;

const isGroup = (e: NavEntry): e is NavGroup => "children" in e;

const navKey = (e: NavEntry) => (isGroup(e) ? `group:${e.groupKey}` : e.path);

/**
 * Sidebar running order, in bands.
 *
 * The old order was roughly the order features got built: Licensing sat sixth,
 * above Devices, and Calendar fell between HelpDesk and Licensing. Sixteen
 * flat items in arbitrary order means every look-up is a linear scan.
 *
 * Two things fix that. FREQUENCY — the daily loop (what needs doing, what is
 * broken) goes at the top, and one-time configuration at the bottom where
 * people already expect settings to live; primacy and recency do the work.
 * CHUNKING — items are grouped into short labelled bands of two to four, so
 * the eye lands on a band first and scans three items, not sixteen.
 *
 * Anything not listed here still renders, appended at the end, so adding a nav
 * entry can never make it vanish — it just lands in "More" until it is placed.
 */
const NAV_BANDS: { section: string; keys: string[] }[] = [
  { section: "", keys: ["/", "/my-dashboards"] },
  // /calendar left this band when it became PSA -> Schedule. Listing it here
  // as well would place it in two bands at once.
  { section: "Service desk", keys: ["group:helpdesk", "group:monitoring"] },
  {
    section: "Infrastructure",
    keys: [
      // Discovery first: it is where a new install starts and where you go
      // when something is missing from the fleet, so it should be the first
      // thing under Infrastructure rather than the second.
      "/network-discovery",
      "group:devices",
      "group:network",
      "group:cloud",
      "group:m365",
      "group:identity",
    ],
  },
  { section: "Business", keys: ["/customers", "group:psa", "group:reporting"] },
  {
    section: "Platform",
    keys: ["group:ai", "group:integrations", "/licensing", "group:admin"],
  },
  // Unlabelled trailing band: the two entries every role gets and none of
  // them configures - your own security, and the manual.
  { section: "", keys: ["/security", "/help"] },
];

const NAV_RANK = new Map<string, number>();
const NAV_SECTION = new Map<string, string>();
NAV_BANDS.forEach((band) => {
  band.keys.forEach((k) => {
    NAV_RANK.set(k, NAV_RANK.size);
    NAV_SECTION.set(k, band.section);
  });
});

const nav = [
  {
    path: "/",
    label: "Dashboard",
    icon: "M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6",
  },
  {
    path: "/my-dashboards",
    label: "My Dashboards",
    icon: "M4 5a1 1 0 011-1h4a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1v-4zM14 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM14 13a1 1 0 011-1h4a1 1 0 011 1v6a1 1 0 01-1 1h-4a1 1 0 01-1-1v-6z",
  },
  {
    path: "/customers",
    label: "Customers",
    icon: "M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0",
  },
  {
    groupKey: "helpdesk",
    label: "HelpDesk",
    icon: "M18.364 5.636a9 9 0 010 12.728m0 0l-2.829-2.829m2.829 2.829L21 21M15.536 8.464a5 5 0 010 7.072m0 0l-2.829-2.829m-4.243 2.829l-2.828 2.828M3 3l3.536 3.536m0 0a5 5 0 000 7.072m0-7.072L3.879 3.879",
    children: [
      {
        path: "/tickets",
        label: "Tickets",
        icon: "M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z",
      },
      {
        // Lives under HelpDesk, not Integrations: it is the log of what inbound
        // support mail became (ticket / reply / blocked / dropped) plus the
        // blocked-sender rules. Nothing about it is a third-party integration
        // now that the appliance receives its own mail.
        path: "/helpdesk/email-intake",
        label: "Email intake",
        icon: "M3 8l9 6 9-6m-18 0a2 2 0 012-2h14a2 2 0 012 2m-18 0v10a2 2 0 002 2h14a2 2 0 002-2V8",
      },
      {
        // Helpdesk policy - SLA targets, moved off Admin -> Settings where it
        // sat among logo uploads and installers. See pages/HelpDeskSettings.tsx.
        path: "/helpdesk/settings",
        label: "Settings",
        icon: "M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z",
      },
      {
        // The mail wizard: derived address, DNS/MX checks, port 25, who gets
        // notified, filtering, outgoing SPF/DKIM/PTR.
        //
        // Labelled "Mail setup", not "Setup". Sitting next to "Settings" the
        // bare word was a coin flip - two adjacent items whose names are near
        // synonyms, one a one-time wizard and one everyday policy. Naming what
        // it configures costs nothing and removes the guess. Route unchanged.
        path: "/helpdesk/setup",
        label: "Mail setup",
        icon: "M3 8l9 6 9-6m-18 0a2 2 0 012-2h14a2 2 0 012 2m-18 0v10a2 2 0 002 2h14a2 2 0 002-2V8",
      },
    ],
  },
  {
    path: "/licensing",
    label: "Licensing",
    icon: "M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z",
  },
  {
    groupKey: "devices",
    label: "Devices",
    icon: "M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17H3a2 2 0 01-2-2V5a2 2 0 012-2h14a2 2 0 012 2v10a2 2 0 01-2 2h-2",
    children: [
      {
        path: "/devices",
        label: "All devices",
        icon: "M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17H3a2 2 0 01-2-2V5a2 2 0 012-2h14a2 2 0 012 2v10a2 2 0 01-2 2h-2",
      },
      {
        path: "/software",
        label: "Software",
        icon: "M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253",
      },
      {
        path: "/scripts",
        label: "Scripts",
        icon: "M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z",
      },
      { path: "/remediation", label: "Self-Healing", icon: "M13 10V3L4 14h7v7l9-11h-7z" },
    ],
  },
  {
    groupKey: "network",
    label: "Network",
    icon: "M5.636 18.364a9 9 0 010-12.728m12.728 0a9 9 0 010 12.728m-9.9-2.829a5 5 0 010-7.07m7.072 0a5 5 0 010 7.07M13 12a1 1 0 11-2 0 1 1 0 012 0z",
    children: [
      {
        path: "/network",
        label: "Network devices",
        icon: "M5.636 18.364a9 9 0 010-12.728m12.728 0a9 9 0 010 12.728m-9.9-2.829a5 5 0 010-7.07m7.072 0a5 5 0 010 7.07M13 12a1 1 0 11-2 0 1 1 0 012 0z",
      },
      {
        path: "/meraki-cloud",
        label: "Cloud controllers",
        icon: "M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z",
      },
      {
        // The screen existed and nothing linked to it. /meraki-licenses had a
        // route, a licence-feature mapping and an AI Assist entry, but no nav
        // item - so the only way in was typing the URL, and the help guide
        // told people to look for a sidebar entry that was not there.
        //
        // Labelled by what it DOES, not by the vendor whose API happens to
        // feed it today: the alerting behind it already spans manually
        // entered licences from any vendor.
        path: "/meraki-licenses",
        label: "Licence renewals",
        icon: "M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z",
      },
    ],
  },
  {
    // Top level, not nested under Network. Discovery scans a subnet and finds
    // EVERYTHING on it - workstations, servers, printers, switches - and the
    // integrations alongside it register devices from cloud vendors. Filing that
    // under "Network" implied it was only about network gear, which is the
    // narrowest part of what it does and hid the main route to onboarding a
    // whole estate.
    path: "/network-discovery",
    label: "Discovery & integrations",
    icon: "M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z",
  },
  {
    groupKey: "monitoring",
    label: "Monitoring & Alerts",
    icon: "M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9",
    children: [
      {
        path: "/alerts",
        label: "Active alerts",
        icon: "M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9",
      },
      {
        path: "/monitoring/maintenance",
        label: "Maintenance schedules",
        icon: "M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z M15 12a3 3 0 11-6 0 3 3 0 016 0z",
      },
      {
        path: "/monitoring/reboots",
        label: "Reboot schedules",
        icon: "M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15",
      },
      {
        path: "/monitoring/certificates",
        label: "Certificates",
        icon: "M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z",
      },
      {
        path: "/monitoring/urls",
        label: "Websites / URLs",
        icon: "M21 12a9 9 0 11-18 0 9 9 0 0118 0z M3.6 9h16.8 M3.6 15h16.8 M12 3a15 15 0 010 18 M12 3a15 15 0 000 18",
      },
      {
        path: "/monitoring/settings",
        label: "Settings",
        icon: "M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z M15 12a3 3 0 11-6 0 3 3 0 016 0z",
      },
    ],
  },
  {
    groupKey: "psa",
    label: "PSA",
    icon: "M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z",
    children: [
      {
        path: "/psa/contracts",
        label: "Contracts",
        icon: "M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z",
      },
      {
        // Before invoices, because that is the order the work happens in:
        // you price it, they agree, then you bill it.
        path: "/psa/quotes",
        label: "Quotes",
        icon: "M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z M12 8v1m0 6v1",
      },
      {
        path: "/psa/invoices",
        label: "Invoices",
        icon: "M9 14l6-6m-5.5.5h.01m4.99 5h.01M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16l3.5-2 3.5 2 3.5-2 3.5 2z",
      },
      {
        path: "/psa/pipeline",
        label: "Pipeline",
        icon: "M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6",
      },
      {
        path: "/psa/rate-cards",
        label: "Rate cards",
        icon: "M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z",
      },
      {
        path: "/psa/ar-aging",
        label: "AR aging",
        icon: "M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z",
      },
      {
        // Moved in here from the top level: scheduling a technician is a PSA
        // activity, and the calendar it lands on is the same object that
        // becomes their Outlook subscription. Route unchanged at /calendar
        // so existing links and bookmarks still work.
        path: "/calendar",
        label: "Schedule",
        icon: "M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z",
      },
    ],
  },
  {
    // Cloud, as its own group under Infrastructure. The generated
    // integrations reference pointed at a "Cloud connectors" screen that did
    // not exist - the adapters and forty-odd endpoints were all there, with
    // no way into them but the storage browser. The children mirror the
    // capability GROUPS the adapters declare (see
    // services/cloud/capabilities.py), so the menu is a consequence of what
    // the code can do rather than a second list to keep in step.
    groupKey: "cloud",
    label: "Cloud",
    icon: "M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z",
    children: [
      {
        path: "/cloud",
        label: "Connectors",
        icon: "M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1",
      },
      {
        path: "/cloud/compute",
        label: "Compute",
        icon: "M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z",
      },
      {
        // The existing multi-provider object-storage browser. It lists
        // connectors, so it belongs beside the screen that creates them.
        path: "/cloud-storage",
        label: "Storage",
        icon: "M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4",
      },
      {
        path: "/cloud/network",
        label: "Networking",
        icon: "M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9",
      },
      {
        // The fourth capability group. It had no screen, which made Google
        // Workspace the one platform you could connect and then not use -
        // the adapter has had full user and group management all along.
        path: "/cloud/identity",
        label: "Users & groups",
        icon: "M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z",
      },
    ],
  },
  {
    groupKey: "m365",
    label: "Microsoft 365",
    // No adminOnly here on purpose: visibility comes from the `m365` feature
    // in Admin -> Roles & access, so it can be granted to a technician or a
    // viewer. adminOnly would override the matrix and make that switch inert.
    icon: "M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z",
    children: [
      {
        path: "/m365",
        label: "M365 dashboard",
            icon: "M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z",
      },
      {
        path: "/m365-tenants",
        label: "Tenants",
        // No longer adminOnly. Its endpoints admit technicians now, so the
        // RBAC matrix decides who sees this - same as the route guard in
        // App.tsx. Both gates move together or the menu offers a page the
        // router bounces.
            icon: "M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z",
      },
    ],
  },
  {
    groupKey: "identity",
    label: "Identity & Tools",
    icon: "M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01",
    children: [
      {
        path: "/active-directory",
        label: "Active Directory",
        icon: "M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197",
      },
      {
        path: "/tools/dns",
        label: "DNS",
        icon: "M21 12a9 9 0 11-18 0 9 9 0 0118 0z M3.6 9h16.8 M3.6 15h16.8 M12 3a15 15 0 010 18 M12 3a15 15 0 000 18",
      },
      {
        path: "/tools/dhcp",
        label: "DHCP",
        icon: "M5.636 18.364a9 9 0 010-12.728m12.728 0a9 9 0 010 12.728m-9.9-2.829a5 5 0 010-7.07m7.072 0a5 5 0 010 7.07M13 12a1 1 0 11-2 0 1 1 0 012 0z",
      },
      {
        path: "/tools/gpo",
        label: "Group Policy",
        icon: "M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z",
      },
    ],
  },
  {
    groupKey: "ai",
    label: "AI",
    icon: "M13 10V3L4 14h7v7l9-11h-7z",
    children: [
      {
        path: "/ai-actions",
        label: "AI Actions",
        icon: "M13 10V3L4 14h7v7l9-11h-7z",
      },
      {
        path: "/intune",
        label: "Intune devices",
        icon: "M9 17.25v1.007a3 3 0 01-.879 2.122L7.5 21h9l-.621-.621A3 3 0 0115 18.257V17.25m6-12V15a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 15V5.25m18 0A2.25 2.25 0 0018.75 3H5.25A2.25 2.25 0 003 5.25m18 0V12a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 12V5.25",
      },
      {
        path: "/mobile",
        label: "Mobile dashboard",
        icon: "M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z",
      },
      {
        path: "/ai-terminal",
        label: "AI Terminal",
        icon: "M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z",
      },
      {
        path: "/ai-settings",
        label: "AI Settings",
        icon: "M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z",
      },
    ],
  },
  {
    groupKey: "integrations",
    label: "Integrations",
    // No group-level adminOnly: the renderer never reads it (it filters
    // LEAVES and hides an empty group). Every child here carries its own.
    icon: "M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1",
    children: [
      {
        path: "/integrations",
        label: "All integrations",
        icon: "M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1",
      },
      {
        path: "/psa-integrations",
        label: "External PSA",
        icon: "M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1",
      },
      {
        // ONE entry. There used to be two adjacent ones - "Alert destinations"
        // and "Slack / Teams" - carrying the two DIRECTIONS of the same
        // integration, and since each vendor names its half after the other
        // end of the connection, nobody could tell which screen wanted the URL
        // they were holding. Split by direction inside the page instead.
        path: "/integrations/chat",
        label: "Slack & Teams",
        icon: "M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z",
      },
    ],
  },
  {
    // EPIC B1. These three were scattered across Business, Devices and AI, so
    // the compliance evidence an auditor asks for lived under an "AI" menu.
    // Grouped by what the user is trying to DO — show somebody the numbers.
    groupKey: "reporting",
    // "Reporting & compliance" wrapped to two lines in the sidebar, which no
    // sibling group does — a group label has less width than a leaf because of
    // the chevron, so the budget is ~20 characters.
    label: "Reports & compliance",
    icon: "M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z",
    children: [
      {
        path: "/reports",
        label: "Reports",
        icon: "M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z",
      },
      {
        // Any report on a cadence — standard, AI summary or generated
        // document. Sits next to Reports because that is where somebody looks
        // for "send this monthly", not under Settings.
        path: "/scheduled-documents",
        label: "Scheduled reports",
        icon: "M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z",
      },
      {
        // Was "AI Compliance". The AI produces the assessment; it is not what
        // the feature IS, and naming the mechanism is what buried it.
        path: "/ai-compliance",
        label: "Compliance",
        icon: "M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z",
      },
      {
        // Flapping devices: the cause behind a lot of alert noise, so it lives
        // with the reports rather than buried in a device view.
        path: "/device-stability",
        label: "Device stability",
        icon: "M13 10V3L4 14h7v7l9-11h-7z",
      },
      {
        path: "/patch-compliance",
        label: "Patch compliance",
        icon: "M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z",
      },
    ],
  },
  {
    groupKey: "admin",
    label: "Admin",
    // No group-level adminOnly: the renderer never reads it (it filters
    // LEAVES and hides an empty group). Every child here carries its own.
    icon: "M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z",
    children: [
      {
        path: "/users",
        label: "Users",
        adminOnly: true,
        icon: "M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197",
      },
      {
        path: "/roles-access",
        label: "Roles & access",
        adminOnly: true,
        icon: "M12 11c0 3.517-1.009 6.799-2.753 9.571m-3.44-2.04l.054-.09A13.916 13.916 0 008 11a4 4 0 118 0c0 1.017-.07 2.019-.203 3m-2.118 6.844A21.88 21.88 0 0015.171 17m3.839 1.132c.645-2.266.99-4.659.99-7.132A8 8 0 008 4.07M3 15.364c.64-1.319 1-2.8 1-4.364 0-1.457.39-2.823 1.07-4",
      },
      {
        path: "/audit",
        label: "Audit Log",
        adminOnly: true,
        icon: "M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z",
      },
      {
        path: "/settings",
        label: "Settings",
        adminOnly: true,
        icon: "M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z M15 12a3 3 0 11-6 0 3 3 0 016 0z",
      },
      {
        path: "/license",
        label: "License",
        adminOnly: true,
        icon: "M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z",
      },
      {
        path: "/backup",
        label: "Backup & Restore",
        adminOnly: true,
        icon: "M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4",
      },
    ],
  },
  {
    // Every role, not just admins. The route is <Protected> (all roles) and
    // Protected redirects anyone with mfa_required HERE, so a technician told
    // to enrol had been sent to a page with no menu entry.
    //
    // Deliberately NOT in the "Admin" group any more: a technician does not
    // administer AeroGauge, and a group called "Admin" holding one item -
    // their own MFA - invites exactly the wrong click. It sits at the foot
    // beside Help, the other thing every role needs and none configures.
    path: "/security",
    label: "Security & MFA",
    icon: "M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z",
  },
  {
    // Deliberately OUTSIDE Admin and last in the sidebar. Help is for every
    // role, not just admins, and the foot of the menu is where people look for
    // it. Previously it was only a button on Admin -> Settings, which techs
    // cannot even open.
    path: "/help",
    label: "Help",
    icon: "M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z",
  },
];

// Nav paths gated by a license feature flag, mapped to the backend module that
// serves them (verified against each page's API prefix vs. the require_feature
// wiring in server/main.py). A leaf is hidden when the license excludes its
// feature. Trial / unlicensed / empty-feature licenses include everything
// (useLicense().hasFeature returns true), so nothing hides in those cases.
// Pages that also have an ungated data source (e.g. AI Compliance reads the
// ungated /api/v1/compliance) are intentionally NOT listed — they still work.
const FEATURE_BY_PATH: Record<string, string> = {
  "/psa/contracts": "psa",
  "/psa/invoices": "psa",
  "/psa/pipeline": "psa",
  "/psa/rate-cards": "psa",
  "/psa/ar-aging": "psa",
  "/ai-actions": "ai",
  "/ai-settings": "ai",
  "/ai-terminal": "ai",
  "/m365": "cloud",
  "/m365-tenants": "cloud",
  "/entra-users": "cloud",
  "/intune": "cloud",
  "/network": "network",
  "/network-discovery": "network",
  "/meraki-cloud": "network",
  "/meraki-licenses": "network",
};

export default function Layout({ children }: { children: React.ReactNode }) {
  const { user, logout } = useAuth();
  const { hasFeature } = useLicense();
  const branding = useBranding();
  const { currentCustomerId } = useCustomer();
  const location = useLocation();
  const [demoMode, setDemoMode] = useState<boolean>(branding.demo_mode);
  const [demoToggleBusy, setDemoToggleBusy] = useState(false);

  // Keep local demo state in sync with the branding context — branding
  // refreshes on mount and after a toggle, so this stays current across
  // tabs that toggle independently.
  useEffect(() => {
    setDemoMode(branding.demo_mode);
  }, [branding.demo_mode]);

  const toggleDemoMode = async () => {
    if (demoToggleBusy) return;
    const next = !demoMode;
    setDemoToggleBusy(true);
    try {
      await api.put("/api/v1/settings/demo_mode", { value: next ? "true" : "false" });
      setDemoMode(next);
      // Pull fresh branding so any other open tab eventually catches up
      // when its window regains focus and re-renders the banner.
      branding.refresh().catch(() => {});
    } catch {
      /* leave toggle as-is; the user will see no change and can retry */
    } finally {
      setDemoToggleBusy(false);
    }
  };
  // Logo + name come from the single branding source of truth (has_logo is a
  // real file-existence check server-side, and logo_url is cache-busted). We
  // no longer derive logo visibility from the separate logo_path setting,
  // which could disagree with whether the file actually exists. logoFailed is
  // a belt-and-suspenders guard so a missing file renders the initials block
  // instead of a broken-image icon.
  const [logoFailed, setLogoFailed] = useState(false);
  const companyName = branding.company_name || "AeroGauge";
  const logoUrl = !logoFailed && branding.has_logo ? branding.logo_url : null;
  const [showPwModal, setShowPwModal] = useState(false);
  const [aiOn, setAiOn] = useState<boolean | null>(null);
  // Mobile sidebar drawer: closed by default. Above md the sidebar is
  // always visible (static layout) and this state is ignored.
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  // Master refresh: bumping this nonce re-keys the page content below, which
  // remounts the active page and re-runs its data-loading effects — an
  // in-place content refresh (no full browser reload, no per-page wiring).
  const [refreshNonce, setRefreshNonce] = useState(0);
  const triggerRefresh = () => setRefreshNonce((n) => n + 1);

  // Expanded-group state for the collapsible sidebar tree. Persisted
  // to localStorage so the user's last expand/collapse stance survives
  // refresh. Versioned key (`_v1`) so a future re-grouping doesn't
  // resurrect stale entries.
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(() => {
    try {
      const raw = localStorage.getItem("nav_expanded_v1");
      if (!raw) return new Set();
      const arr = JSON.parse(raw);
      return new Set(Array.isArray(arr) ? arr : []);
    } catch {
      return new Set();
    }
  });

  const toggleGroup = (groupKey: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupKey)) next.delete(groupKey);
      else next.add(groupKey);
      try {
        localStorage.setItem("nav_expanded_v1", JSON.stringify(Array.from(next)));
      } catch {
        /* localStorage full or disabled — non-fatal */
      }
      return next;
    });
  };

  const forcedChange = !!user?.must_change_password;

  // Reset the failure guard whenever the logo URL changes (e.g. an admin
  // re-uploads and branding refreshes with a new cache-busting version), so
  // a previously-broken logo gets another chance to load.
  useEffect(() => {
    setLogoFailed(false);
  }, [branding.logo_url]);

  useEffect(() => {
    if (forcedChange) setShowPwModal(true);
  }, [forcedChange]);

  useEffect(() => {
    api
      .get("/api/v1/ai/flags")
      .then((r) => setAiOn(!!r.data.ai_enabled))
      // null, NOT false. `aiOn` is boolean | null and the badge below already
      // renders nothing while it is null - the design was right. Setting false
      // on failure defeated that guard and made a request we could not complete
      // assert "AI OFF" in the sidebar of every page. That is what the owner of
      // the system saw when a transient error made him believe someone had
      // switched AI off; the AI Settings page said the same thing at the same
      // time, so two independent lies agreed with each other.
      .catch(() => setAiOn(null));
  }, []);

  // Close the mobile drawer when the route changes — otherwise tapping
  // a nav item leaves the drawer open over the new page.
  useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname]);

  // Auto-expand the group containing the current route so the active
  // leaf is always visible. We do this additively — never collapse a
  // group the user already opened. The dependency on location.pathname
  // re-runs on every navigation so deep-linking into /ai-actions etc.
  // pops the AI group open even on first load.
  useEffect(() => {
    const activeGroupKey = (nav as NavEntry[]).find(
      (e) =>
        isGroup(e) &&
        e.children.some(
          (c) =>
            location.pathname === c.path ||
            (c.path !== "/" && location.pathname.startsWith(c.path)),
        ),
    );
    if (activeGroupKey && isGroup(activeGroupKey)) {
      setExpandedGroups((prev) => {
        if (prev.has(activeGroupKey.groupKey)) return prev;
        const next = new Set(prev);
        next.add(activeGroupKey.groupKey);
        try {
          localStorage.setItem("nav_expanded_v1", JSON.stringify(Array.from(next)));
        } catch {
          /* ignore */
        }
        return next;
      });
    }
  }, [location.pathname]);

  if (!user) return <Navigate to="/login" replace />;

  // Filter the tree: drop adminOnly leaves for non-admins, then drop
  // any group whose children all got filtered out. We re-run this on
  // every render rather than memoising — the user object's role is
  // stable per session, so the cost is negligible.
  const isAdmin = user.role === "admin";

  // Per-ROLE feature access (Admin → Roles & access), which is a different
  // question from LicenseContext.hasFeature above — that one is "did the MSP
  // pay for this", this one is "may this role use it". Both must say yes.
  //
  // The catalogue lives on the server and arrives with /me, so there is no
  // second copy here to drift out of step. Longest route prefix wins, for the
  // same reason the server matches that way: /reports-builder must not resolve
  // via /reports.
  const roleRoutes: [string, string][] = [];
  for (const [key, routes] of Object.entries(user.feature_routes || {})) {
    for (const r of routes) roleRoutes.push([r, key]);
  }
  roleRoutes.sort((a, b) => b[0].length - a[0].length);
  const roleFeatures = user.features;
  const allowedByRole = (path: string) => {
    // No list (an older token, or a login response that predates the field) →
    // do not hide anything. The server is the enforcement point; this is the
    // menu, and a menu that hides things because a field was missing is worse
    // than one that shows something which then 403s.
    if (!roleFeatures) return true;
    const hit = roleRoutes.find(([prefix]) =>
      prefix === "/" ? path === "/" : path === prefix || path.startsWith(prefix + "/"),
    );
    return !hit || roleFeatures.includes(hit[1]);
  };

  /** Which child of a group the current URL belongs to.
   *
   * Prefix matching alone is not enough once one child's path is a prefix of
   * another's: on /cloud/identity both "Connectors" (/cloud) and
   * "Users & groups" highlighted. The longest match is the specific one, and
   * the specific one is where the user actually is. */
  const activeChildPath = (children: NavLeaf[]): string | null => {
    const path = location.pathname;
    const matches = children.filter(
      (c) => path === c.path || (c.path !== "/" && path.startsWith(c.path + "/")),
    );
    if (!matches.length) return null;
    return matches.reduce((a, b) => (b.path.length > a.path.length ? b : a)).path;
  };

  const allowLeaf = (c: NavLeaf) => {
    if (!isAdmin && c.adminOnly) return false;
    if (!allowedByRole(c.path)) return false;
    const feature = FEATURE_BY_PATH[c.path];
    return !feature || hasFeature(feature);
  };
  const visibleNav: NavEntry[] = (nav as NavEntry[])
    .map((e) => {
      if (isGroup(e)) {
        const kids = e.children.filter(allowLeaf);
        if (kids.length === 0) return null;
        return { ...e, children: kids } as NavGroup;
      }
      return allowLeaf(e) ? e : null;
    })
    .filter((e): e is NavEntry => e !== null)
    // Ordered by NAV_BANDS. An unplaced entry keeps a rank past the end so it
    // appends rather than disappearing.
    .sort((a, b) => {
      const ra = NAV_RANK.has(navKey(a)) ? (NAV_RANK.get(navKey(a)) as number) : Number.MAX_SAFE_INTEGER;
      const rb = NAV_RANK.has(navKey(b)) ? (NAV_RANK.get(navKey(b)) as number) : Number.MAX_SAFE_INTEGER;
      return ra - rb;
    });

  return (
    <div className="flex flex-col h-screen bg-gray-50 dark:bg-gray-900 overflow-hidden">
      {/* Demo-mode banner — full-width, sits above EVERYTHING (including
          the sidebar) so an unfamiliar viewer can't miss it. Yellow
          regardless of MSP brand color; the brand identity stays on the
          sidebar / accent buttons. */}
      {demoMode && (
        <div
          className="bg-yellow-400 text-yellow-950 border-b-2 border-yellow-600 shrink-0
                     px-3 py-1.5 text-sm font-semibold flex items-center justify-center gap-3
                     shadow-sm"
          role="status"
          aria-live="polite"
        >
          <span className="text-base">⚠</span>
          <span>
            DEMO MODE — outbound emails, push notifications, and Intune privileged ops are blocked.
          </span>
          {user?.role === "admin" && (
            <button
              onClick={toggleDemoMode}
              disabled={demoToggleBusy}
              className="text-xs underline hover:no-underline disabled:opacity-50"
            >
              {demoToggleBusy ? "…" : "Turn OFF"}
            </button>
          )}
        </div>
      )}

      <div className="flex flex-1 overflow-hidden min-h-0">
        {/* Mobile-only backdrop for the slide-out drawer. Tapping it closes
          the drawer; transitions cover both opacity and pointer-events
          so the backdrop doesn't intercept clicks when hidden. */}
        <div
          className={`fixed inset-0 bg-black/40 z-30 md:hidden transition-opacity ${
            mobileNavOpen ? "opacity-100" : "opacity-0 pointer-events-none"
          }`}
          onClick={() => setMobileNavOpen(false)}
          aria-hidden="true"
        />

        {/* Sidebar.
            - md+: static column, always visible
            - <md: fixed overlay that slides in from the left */}
        <div
          className={`bg-slate-900 text-white flex flex-col shrink-0 z-40 transition-transform
          fixed inset-y-0 left-0 w-64 transform ${mobileNavOpen ? "translate-x-0" : "-translate-x-full"}
          md:static md:translate-x-0 md:w-60`}
        >
          <div className="px-4 py-5 border-b border-slate-700 flex items-center gap-3">
            {logoUrl ? (
              /* 44px rather than 32px. At the smaller size the mark was barely
                 legible against the dark sidebar; rounded corners let a square
                 logo on a solid background read as an app icon instead of a
                 pasted rectangle. */
              <img
                src={logoUrl}
                alt={companyName}
                className="h-11 w-auto max-w-[3.5rem] object-contain rounded-lg shrink-0"
                onError={() => setLogoFailed(true)}
              />
            ) : (
              <div className="w-11 h-11 shrink-0 bg-blue-500 rounded-lg flex items-center justify-center text-white font-bold text-base">
                {companyName.charAt(0)}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="text-sm font-bold leading-tight truncate">{companyName}</div>
              <div className="text-xs text-slate-400 flex items-center gap-1.5">
                {/* The PRODUCT under the operator's name. Was "RMM Platform",
                    a category noun that told nobody anything and left the
                    product itself unnamed on its own console. */}
                {PRODUCT_NAME}
                {aiOn !== null && (
                  <span
                    title={aiOn ? "AI mode is ON" : "AI mode is OFF"}
                    className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold ${
                      aiOn ? "bg-violet-500 text-white" : "bg-slate-700 text-slate-400"
                    }`}
                  >
                    AI {aiOn ? "ON" : "OFF"}
                  </span>
                )}
              </div>
            </div>
            {/* Close-X visible only when the mobile drawer is open */}
            <button
              onClick={() => setMobileNavOpen(false)}
              className="md:hidden text-slate-400 hover:text-white"
              aria-label="Close menu"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Demo-mode tile. Sits right under the brand block so it's the
            first thing anyone sees in the sidebar. Admin-only toggle;
            for non-admins it's read-only status text when ON, hidden
            when OFF. */}
          {(user?.role === "admin" || demoMode) && (
            <div className="px-3 pt-3">
              {user?.role === "admin" ? (
                <button
                  onClick={toggleDemoMode}
                  disabled={demoToggleBusy}
                  title={
                    demoMode
                      ? "Demo mode is ON — outbound emails / push / Intune privileged ops are blocked. Click to disable."
                      : "Turn on demo mode for sales / training. Blocks outbound side effects; banner shows on every screen."
                  }
                  className={`w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg text-sm font-semibold transition-colors disabled:opacity-50 ${
                    demoMode
                      ? "bg-yellow-400 text-yellow-950 hover:bg-yellow-300 ring-2 ring-yellow-300 shadow-lg"
                      : "bg-slate-800 text-slate-200 hover:bg-slate-700 border border-slate-700"
                  }`}
                >
                  <span className="flex items-center gap-2">
                    <span className="text-base">{demoMode ? "⚠" : "🎬"}</span>
                    <span>{demoMode ? "DEMO MODE ON" : "Demo mode"}</span>
                  </span>
                  <span
                    className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded ${
                      demoMode ? "bg-yellow-950 text-yellow-100" : "bg-slate-700 text-slate-400"
                    }`}
                  >
                    {demoToggleBusy ? "…" : demoMode ? "ON" : "OFF"}
                  </span>
                </button>
              ) : (
                <div
                  className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold
                           bg-yellow-400 text-yellow-950 ring-2 ring-yellow-300 shadow-lg"
                  title="Demo mode is on — outbound side effects are blocked. Ask an admin to disable."
                >
                  <span className="text-base">⚠</span>
                  <span>DEMO MODE ON</span>
                </div>
              )}
            </div>
          )}

          <nav className="flex-1 px-2 py-4 space-y-0.5 overflow-y-auto">
            {visibleNav.map((entry, idx) => {
              // Band label, drawn when this entry starts a new band. Computed
              // from the FILTERED list, so a band whose every item is hidden
              // from this role leaves no orphaned heading behind.
              const section = NAV_SECTION.get(navKey(entry)) || "";
              const prevSection =
                idx === 0 ? null : NAV_SECTION.get(navKey(visibleNav[idx - 1])) || "";
              const heading =
                section && section !== prevSection ? (
                  <div
                    key={`sec:${section}`}
                    className="px-3 pt-4 pb-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-500 select-none"
                  >
                    {section}
                  </div>
                ) : null;

              if (isGroup(entry)) {
                const open = expandedGroups.has(entry.groupKey);
                const hasActiveChild = entry.children.some(
                  (c) =>
                    location.pathname === c.path ||
                    (c.path !== "/" && location.pathname.startsWith(c.path + "/")),
                );
                return (
                  <React.Fragment key={entry.groupKey}>
                    {heading}
                    <div>
                    <button
                      type="button"
                      onClick={() => toggleGroup(entry.groupKey)}
                      aria-expanded={open}
                      className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                        hasActiveChild
                          ? "text-white bg-slate-800/60"
                          : "text-slate-300 hover:bg-slate-800 hover:text-white"
                      }`}
                    >
                      <svg
                        className="w-5 h-5 shrink-0"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={1.75}
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" d={entry.icon} />
                      </svg>
                      <span className="flex-1 text-left">{entry.label}</span>
                      <svg
                        className={`w-4 h-4 shrink-0 transition-transform ${open ? "rotate-90" : ""}`}
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={2}
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                    {open && (
                      <div className="mt-0.5 mb-1 pl-3 ml-3 border-l border-slate-700 space-y-0.5">
                        {entry.children.map((child) => {
                          // THE LONGEST MATCHING SIBLING WINS, not every one
                          // that prefix-matches. Cloud is the first group
                          // where a child's path is a prefix of another's -
                          // "/cloud" (Connectors) against "/cloud/identity" -
                          // and a plain prefix test lit BOTH entries at once,
                          // so the menu claimed you were in two places.
                          const active = activeChildPath(entry.children) === child.path;
                          return (
                            <Link
                              key={child.path}
                              to={child.path}
                              className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm transition-colors ${
                                active
                                  ? "bg-blue-600 text-white font-medium"
                                  : "text-slate-300 hover:bg-slate-800 hover:text-white"
                              }`}
                            >
                              <svg
                                className="w-4 h-4 shrink-0 opacity-80"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth={1.75}
                                viewBox="0 0 24 24"
                              >
                                <path strokeLinecap="round" strokeLinejoin="round" d={child.icon} />
                              </svg>
                              {child.label}
                            </Link>
                          );
                        })}
                      </div>
                    )}
                    </div>
                  </React.Fragment>
                );
              }
              // Leaf at top level (e.g. Dashboard, Customers, Tickets).
              const active =
                location.pathname === entry.path ||
                (entry.path !== "/" && location.pathname.startsWith(entry.path + "/"));
              return (
                <React.Fragment key={entry.path}>
                  {heading}
                  <Link
                    to={entry.path}
                    className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      active
                        ? "bg-blue-600 text-white"
                        : "text-slate-300 hover:bg-slate-800 hover:text-white"
                    }`}
                  >
                    <svg
                      className="w-5 h-5 shrink-0"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={1.75}
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d={entry.icon} />
                    </svg>
                    {entry.label}
                  </Link>
                </React.Fragment>
              );
            })}
          </nav>

          {/* Footer kept minimal: who's signed in + Sign out. Appearance,
            Security, Change password, Help, and Install app moved into
            Settings to declutter the sidebar. */}
          <div className="px-4 py-4 border-t border-slate-700 flex items-center justify-between gap-2">
            <div className="min-w-0">
              <div className="text-xs text-slate-400 truncate">{user.username}</div>
              <div className="text-xs text-slate-500 capitalize">{user.role}</div>
            </div>
            <button
              onClick={logout}
              className="text-xs text-slate-300 hover:text-white border border-slate-700 hover:border-slate-500 rounded-lg px-2.5 py-1 transition-colors shrink-0"
            >
              Sign out
            </button>
          </div>
        </div>

        {/* Main column */}
        <div className="flex-1 flex flex-col overflow-hidden min-w-0">
          {/* Mobile top bar: hamburger + name + customer switcher. Hidden on md+. */}
          <div className="md:hidden flex items-center gap-3 px-3 py-2 bg-slate-900 text-white shrink-0">
            <button
              onClick={() => setMobileNavOpen(true)}
              className="p-1 hover:bg-slate-800 rounded"
              aria-label="Open menu"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
            <div className="text-sm font-semibold truncate flex-1">{companyName}</div>
            <button
              onClick={triggerRefresh}
              title="Refresh this page's content"
              aria-label="Refresh"
              className="p-1.5 text-slate-300 hover:text-white hover:bg-slate-800 rounded"
            >
              <svg
                className="w-5 h-5"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M23 4v6h-6" />
                <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
              </svg>
            </button>
            <CustomerSwitcher />
          </div>
          {/* Desktop header bar: global customer switcher scopes customer-centric
            pages (M365, AI Actions, …). Aggregate pages ignore the selection. */}
          <div className="hidden md:flex items-center justify-between px-6 py-2 bg-white border-b border-gray-200 dark:bg-slate-900 dark:border-slate-700 shrink-0">
            <CustomerSwitcher />
            <button
              onClick={triggerRefresh}
              title="Refresh this page's content"
              className="inline-flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-300 border border-gray-300 dark:border-slate-600 rounded-lg px-3 py-1.5 hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors"
            >
              <svg
                className="w-4 h-4"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M23 4v6h-6" />
                <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
              </svg>
              Refresh
            </button>
          </div>
          <main className="flex-1 overflow-y-auto p-3 md:p-6">
            {/* Screen-aware AI copilot — inline collapsible card at the top of
                every page (like the Identity & Tools assistant), shown when AI
                mode is on. Sits outside the keyed wrapper so its chat state
                survives page refresh / customer switches. */}
            <AIAssist />
            {/* Keyed by the refresh nonce AND the selected customer so the
                master Refresh — and switching customers — remounts the active
                page and re-fetches its data scoped to the current customer. */}
            <div key={`${refreshNonce}:${currentCustomerId ?? "all"}`}>{children}</div>
          </main>
        </div>

        {showPwModal && (
          <ChangePasswordModal
            endpoint="/api/v1/auth/change-password"
            apiClient={api}
            forced={forcedChange}
            onClose={() => {
              if (!forcedChange) setShowPwModal(false);
            }}
            onSuccess={() => {
              setShowPwModal(false);
              if (forcedChange) window.location.reload();
            }}
          />
        )}
      </div>
    </div>
  );
}
